import { IDeptTreeSnapshot } from 'src/models/dept-tree/DeptTree';
import { IModelCodeSnapshot } from 'src/models/model-code/ModelCode';

import { ILoadingStore } from '../models/loading-store/LoadingStore';

export const CallApiToStore = async <T = void>(
  storeFunction: Promise<T>,
  type: 'page' | 'api',
  loadingStore: ILoadingStore,
): Promise<T> => {
  loadingStore.allLoading();
  try {
    // 스토어 액션이 결과를 반환하는 경우(예: 서버 메시지·변경 건수가 필요한 API)
    // 호출한 화면에서 쓸 수 있도록 그대로 전달한다.
    return await storeFunction;
  } finally {
    // 성공/실패/예외(reject) 여부와 무관하게 항상 로딩 카운트를 정리한다.
    // (네트워크 오류·타임아웃·throw 시에도 무한 로딩이 발생하지 않도록 보장)
    loadingStore.closeLoadingNoMsg();
  }
};

interface Message {
  type: string;
  payload?: any;
}

export const sendReactNativeMessage = ({ type, payload }: Message) => {
  // @ts-ignore
  if (window.ReactNativeWebView) {
    // @ts-ignore
    window.ReactNativeWebView.postMessage(JSON.stringify({ type, payload }));
  }
};

export const isMobile = () => {
  const userAgent = typeof navigator === 'undefined' ? 'SSR' : navigator.userAgent;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  // const userAgent = navigator.userAgent;
  // if (userAgent.match(/iPhone|iPod|Android|Windows CE|BlackBerry|Symbian|Windows Phone|webOS|Opera Mini|Opera Mobi|POLARIS|IEMobile|lgtelecom|nokia|SonyEricsson/i) != null || userAgent.match(/LG|SAMSUNG|Samsung/) != null) {
  //     return true;
  // } else {
  //     return false;
  // }
};

const hostname = window.location.hostname;
export const isDemoDomain =
  hostname === 'fcos-demo.surfinn.kr' ||
  hostname === 'demo.fcos.surfinn.kr' ||
  hostname === 'local.surfinn.kr';

export const getPartnerTypeLabel = (type: IModelCodeSnapshot) => {
  if (isDemoDomain) {
    return type.code === 201201 ? '일반' : '협력';
  }
  return type.value?.toUpperCase() || '';
};
export const getPartnerTitleByType = (type: 'ap' | 'msp', suffix = '조회') => {
  const label = isDemoDomain ? (type === 'ap' ? '일반' : '협력') : type === 'ap' ? 'AP' : 'MSP';

  return `파트너사(${label}) ${suffix}`;
};

export const getColorBySeverityCd = (code: number) => {
  switch (code) {
    case 200201:
      return '#E875BE';
    case 200202:
      return '#FEAA76';
    case 200203:
      return '#73C34E';
    case 200204:
      return '#5895F8';
    default:
      break;
  }
};

export const getStageAbbr = (val: string) => {
  switch (val) {
    case '수주완료':
      return '수주';
    case '영업기회발견':
      return '발견';
    case '제품소개,고객요건협의':
      return '소개';
    case '경쟁입찰/투찰':
      return '입찰';
    case '우선협상대상 선정':
      return '선정';
    case '가격협상':
      return '협상';
    default:
      return '';
  }
};

export const getDeptWithSid = (deptList: IDeptTreeSnapshot[], deptSid: number) => {
  // Recursively search for the department by `deptSid`
  for (const dept of deptList) {
    if (dept.deptSid === deptSid) {
      return dept; // Return the department if found
    }
    // Search in child departments recursively
    if (dept.childDept) {
      const foundDept: IDeptTreeSnapshot = getDeptWithSid(dept.childDept, deptSid);
      if (foundDept) {
        return foundDept;
      }
    }
  }
  return null; // Return null if department is not found
};

/**
 * 상위부서 / 현재부서 표시하기
 * @param deptList
 * @param targetDept
 * @returns
 */
export const deptWithParentDepts = (
  deptList: IDeptTreeSnapshot[],
  targetDept: IDeptTreeSnapshot,
) => {
  const getParentDepts = (deptList: IDeptTreeSnapshot[], targetDeptSid: number): string[] => {
    for (const node of deptList) {
      if (node.deptSid === targetDeptSid) {
        return [node.deptNm];
      }
      if (node.childDept && node.childDept.length > 0) {
        const found = getParentDepts(node.childDept, targetDeptSid);
        if (found.length > 0) {
          return [node.deptNm, ...found]; // 상위 부서들을 반환
        }
      }
    }
    return [];
  };
  const currentDeptName = targetDept?.deptNm || '부서 없음';
  const parentDeptNames = getParentDepts(deptList, targetDept?.deptPsid) || ['상위 부서 없음'];

  return [...parentDeptNames, currentDeptName].join(' / ');
};

export const hasChildDepts = (deptList: IDeptTreeSnapshot[], targetDeptSid: number): boolean => {
  for (const node of deptList) {
    if (node.deptSid === targetDeptSid) {
      return node.childDept && node.childDept.length > 0;
    }
    if (node.childDept && node.childDept.length > 0) {
      const hasChild = hasChildDepts(node.childDept, targetDeptSid);
      if (hasChild) return true;
    }
  }
  return false;
};

/**
 * 프로젝트 필드 목록
 */
export const fieldNameMap: Record<string, string> = {
  // 기본정보
  projectTypeCdList: '프로젝트 타입',
  reportYn: '보고 여부',
  progYn: '진행중 여부',
  severityCd: '중요도',
  bo: '사업 담당자',
  newStatusCd: '신규 여부',
  ongoingCd: '지속 여부',
  projectNm: '프로젝트명',
  pm: '프로젝트 담당자',
  pmSid: '프로젝트 담당자 ID',
  dept: '프로젝트 관리 부서',
  deptSid: '부서 ID',
  esmCode: 'ESM코드',
  bizMngCode: '사업관리코드',
  bizDraftNo: '사업기안번호',
  uuid: 'UUID',
  orgId: '조직ID',
  customerCmpy: '고객사',
  mngList: '담당자',
  salesRep: '영업 담당자',
  msp: getPartnerTitleByType('msp', ''),
  ap: getPartnerTitleByType('ap', ''),

  // 계약정보
  contractCmpy: '계약사',
  startDay: '시작일시',
  endDay: '종료일시',
  billCriteriaCd: '청구기준',
  billPeriodCd: '청구시기',
  billChargeCd: '청구여부',
  paymentInfo: '수금정보',
  quotationFile: '견적서파일',
  quotationFilePath: '견적서파일', // 동일한 필드명 처리
  additionalFile: '첨부파일',
  additionalFilePaths: '첨부파일', // 동일한 필드명 처리

  // 수주정보
  orderList: '수주 정보',
  orderAmt: '수주 금액',
  orderMargin: '예상 마진',
  marginPrcnt: '예상 마진율',
  bzStepCd: '영업단계',
  orderDay: '수주시기',
  orderPrcnt: '수주확률',
  orderSCauses: '수주(100%)원인',
  orderFCauses: '실주(0%)원인',
  // cntrctDay: '계약 일자', // ???

  // 매출정보
  salesAmt: '매출 금액',
  roundSalesList: '매출 정보',
  // salesMargin: '매출 마진', // ???

  // 매입정보
  purchaseAmt: '매입 금액',
  purchaseList: '매입 정보',

  descr: '기타 메모',
};
/**
 * 프로젝트 필드명 가져오기
 * @param key
 * @returns
 */
export const getFieldNm = (key: string) => fieldNameMap[key] || key;

// /**
//  * html 변환
//  * @param rawHtml
//  * @returns
//  */
// export function cleanHtmlContent(rawHtml: string): string {
//   const div = document.createElement('div');
//   div.innerHTML = rawHtml;
//   // 텍스트 노드 내 공백 처리
//   const walker = document.createTreeWalker(div, NodeFilter.SHOW_TEXT);
//   while (walker.nextNode()) {
//     const node = walker.currentNode as Text;
//     if (node.nodeValue) {
//       node.nodeValue = node.nodeValue
//         .replace(/\t/g, '\u00A0\u00A0\u00A0\u00A0') // 탭
//         .replace(/ {2}/g, '\u00A0\u00A0') // 2스페이스
//         .replace(/ /g, '\u00A0'); // 단일 스페이스
//     }
//   }
//   const blocks: ChildNode[] = Array.from(div.childNodes);
//   // 앞쪽 공백 제거
//   while (
//     blocks.length > 0 &&
//     blocks[0].nodeType === Node.ELEMENT_NODE &&
//     (blocks[0] as Element).outerHTML === '<p><br></p>'
//   ) {
//     const first = blocks[0];
//     if (first.parentNode) {
//       first.parentNode.removeChild(first);
//       blocks.shift();
//     } else {
//       break;
//     }
//   }
//   // 뒤쪽 공백 제거
//   while (
//     blocks.length > 0 &&
//     blocks[blocks.length - 1].nodeType === Node.ELEMENT_NODE &&
//     (blocks[blocks.length - 1] as Element).outerHTML === '<p><br></p>'
//   ) {
//     const last = blocks[blocks.length - 1];
//     if (last && last.parentNode) {
//       last.parentNode.removeChild(last);
//       blocks.pop();
//     } else {
//       break;
//     }
//   }
//   return div.innerHTML;
// }

// export const normalizeHtml = (html: string) => {
//   return (
//     html
//       .replace(/\s*style="([^"]+)"/g, (_, styleContent) => {
//         // 스타일 속성을 key-value로 파싱 후 알파벳 순으로 정렬
//         const sortedStyle = styleContent
//           .split(';')
//           .map((s: any) => s.trim())
//           .filter(Boolean)
//           .sort()
//           .join('; ');
//         return ` style="${sortedStyle}"`;
//       })
//       // .replace(/\s+/g, ' ') // 공백 정리
//       // .replace(/&nbsp;/g, ' ') // 필요 시 nbsp 도 space로
//       .trim()
//   );
// };

// 정말 필요한 경우만 처리하고, 나머지는 그대로
export function cleanHtmlContent(rawHtml: string): string {
  const div = document.createElement('div');
  div.innerHTML = rawHtml;
  // 텍스트 노드 내 공백 처리
  const walker = document.createTreeWalker(div, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    if (node.nodeValue) {
      node.nodeValue = node.nodeValue
        .replace(/\t/g, '\u00A0\u00A0\u00A0\u00A0') // 탭
        .replace(/ {2}/g, '\u00A0\u00A0') // 2스페이스
        .replace(/ /g, '\u00A0'); // 단일 스페이스
    }
  }
  const blocks: ChildNode[] = Array.from(div.childNodes);
  // 앞쪽 공백 제거
  while (
    blocks.length > 0 &&
    blocks[0].nodeType === Node.ELEMENT_NODE &&
    (blocks[0] as Element).outerHTML === '<p><br></p>'
  ) {
    const first = blocks[0];
    if (first.parentNode) {
      first.parentNode.removeChild(first);
      blocks.shift();
    } else {
      break;
    }
  }
  // 뒤쪽 공백 제거
  while (
    blocks.length > 0 &&
    blocks[blocks.length - 1].nodeType === Node.ELEMENT_NODE &&
    (blocks[blocks.length - 1] as Element).outerHTML === '<p><br></p>'
  ) {
    const last = blocks[blocks.length - 1];
    if (last && last.parentNode) {
      last.parentNode.removeChild(last);
      blocks.pop();
    } else {
      break;
    }
  }
  return div.innerHTML;
}

export const renderHtmlWithTabs = (html: string) =>
  html.replace(/\t/g, '&emsp;').replace(/\s{2,}/g, (match) => match.replace(/ /g, '&nbsp;'));

export function normalizeHtmlAttributes(html: string): string {
  return (
    html
      // class 속성 정렬
      .replace(/class="([^"]+)"/g, (_, classStr) => {
        const sorted = classStr.trim().split(/\s+/).sort().join(' ');
        return `class="${sorted}"`;
      })
      // style 속성 정렬
      .replace(/style="([^"]+)"/g, (_, styleStr) => {
        const sorted = styleStr
          .split(';')
          .map((s: string) => s.trim())
          .filter(Boolean)
          .sort()
          .join('; ');
        return `style="${sorted};"`; // 맨 끝에 세미콜론 붙이는 방식 유지
      })
  );
}

export function formatFilename(template: string, date: Date) {
  const replacements: Record<string, string> = {
    yyyy: String(date.getFullYear()),
    MM: String(date.getMonth() + 1).padStart(2, '0'),
    dd: String(date.getDate()).padStart(2, '0'),
    HH: String(date.getHours()).padStart(2, '0'),
    mm: String(date.getMinutes()).padStart(2, '0'),
    ss: String(date.getSeconds()).padStart(2, '0'),
  };

  return template.replace(/%(\w+)/g, (_, token) => replacements[token] ?? '');
}

// 숫자를 한글로 변환하는 함수
export const numberToKorean = (number: number): string => {
  if (number === 0) return '';
  if (!number) return '';

  // 1000경 이상 체크 (10^16 이상)
  if (number >= 10000000000000000) {
    return '금액이 너무 큽니다';
  }

  const units = ['', '만', '억', '조', '경'];
  const smallUnits = ['', '십', '백', '천'];

  let result = '';
  let unitIndex = 0;

  while (number > 0) {
    const part = number % 10000;

    if (part > 0) {
      let partStr = '';
      let tempPart = part;

      for (let i = 0; i < 4; i++) {
        const digit = tempPart % 10;
        if (digit > 0) {
          if (digit === 1 && i > 0) {
            // 1은 십,백,천에서 생략
            partStr = smallUnits[i] + partStr;
          } else {
            partStr = numberToKoreanDigit(digit) + smallUnits[i] + partStr;
          }
        }
        tempPart = Math.floor(tempPart / 10);
      }

      result = partStr + units[unitIndex] + (result ? ' ' : '') + result;
    }

    number = Math.floor(number / 10000);
    unitIndex++;
  }

  return result.trim() + '원';
};

// 단일 숫자를 한글로 변환
const numberToKoreanDigit = (digit: number): string => {
  const koreanDigits = ['', '일', '이', '삼', '사', '오', '육', '칠', '팔', '구'];
  return koreanDigits[digit];
};

// ─────────────────────────────────────────────────────────────
// URL 파라미터 값 난독화 (Base64)
// - 목적: FC차트 → /project 이동 시 projectSids 값을 주소창에서 숨김 (미관/난독화 수준)
// - 주의: 프론트 전용 인코딩이라 보안(권한) 방어는 아님. 실제 권한 검증은 백엔드 책임.
// - URL-safe Base64 사용(+/= 를 URL에 안전한 문자로 치환)
// ─────────────────────────────────────────────────────────────
export const encodeParamValue = (value: string): string => {
  try {
    // 유니코드 안전 처리 후 base64 → URL-safe 치환
    const base64 = btoa(encodeURIComponent(value));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  } catch {
    return value;
  }
};

export const decodeParamValue = (value: string): string => {
  try {
    // URL-safe 치환 복원 후 padding 보정 → base64 디코딩
    let base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) base64 += '='.repeat(4 - pad);
    return decodeURIComponent(atob(base64));
  } catch {
    // 디코딩 실패(평문이거나 손상된 값)면 원본 그대로 반환
    return value;
  }
};
